import React, { useState, useRef } from "react";
import { Play, Pause, Volume2, VolumeX, Mic, Leaf } from "lucide-react";
import { AnimatedSection } from "./shared";

const M = `${process.env.REACT_APP_BACKEND_URL}/api/media`;

const episodes = [
  { src: `${M}/podcast-1.mp4`, poster: `${M}/podcast-1-poster.jpg`, name: "Dr Suma Geethika", role: "Gynecologist" },
  { src: `${M}/podcast-2.mp4`, poster: `${M}/podcast-2-poster.jpg`, name: "Dr Fahim", role: "Andrologist" },
  { src: `${M}/podcast-3.mp4`, poster: `${M}/podcast-3-poster.jpg`, name: "Dr Harshitha", role: "Gynecologist" },
  { src: `${M}/podcast-4.mp4`, poster: `${M}/podcast-4-poster.jpg`, name: "Dr Naveen", role: "Psychiatrist" },
  { src: `${M}/podcast-5.mp4`, poster: `${M}/podcast-5-poster.jpg`, name: "Dr Rekha", role: "Ayurveda Fertility Specialist" },
  { src: `${M}/podcast-6.mp4`, poster: `${M}/podcast-6-poster.jpg`, name: "Dr Neelima", role: "Gynecologist" },
];

const EpisodeCard = ({ ep, idx }) => {
  const ref = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [loaded, setLoaded] = useState(false);

  const handleLoaded = () => {
    const v = ref.current;
    if (v && !loaded) { v.currentTime = 0.5; setLoaded(true); }
  };

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (playing) { v.pause(); setPlaying(false); }
    else { v.muted = muted; v.play().then(() => setPlaying(true)).catch(() => {}); }
  };

  const toggleMute = (e) => {
    e.stopPropagation();
    const v = ref.current;
    if (!v) return;
    v.muted = !muted;
    setMuted(!muted);
  };

  return (
    <div
      data-testid={`podcast-card-${idx}`}
      className="relative group cursor-pointer flex-shrink-0 w-[260px] snap-center"
      onClick={toggle}
    >
      <div className="relative aspect-[9/16] rounded-2xl overflow-hidden shadow-lg bg-[#0D1F17]/20">
        <video
          ref={ref}
          src={ep.src}
          poster={ep.poster}
          playsInline
          loop
          muted={muted}
          preload="metadata"
          onLoadedData={handleLoaded}
          className="w-full h-full object-cover"
          data-testid={`podcast-player-${idx}`}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-transparent to-black/30 pointer-events-none" />
        <div className="absolute top-3 left-3 flex items-center gap-1.5 bg-[#D4AF37]/90 backdrop-blur-sm text-[#0D1F17] px-3 py-1 rounded-full">
          <Mic size={11} />
          <span className="text-[10px] font-semibold tracking-wide uppercase">Podcast</span>
        </div>
        {!playing && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="w-16 h-16 rounded-full bg-white/15 backdrop-blur-md flex items-center justify-center border border-white/25 group-hover:scale-110 transition-transform">
              <Play size={24} className="text-white ml-1" fill="white" />
            </div>
          </div>
        )}
        {playing && (
          <div className="absolute top-3 right-3 opacity-0 group-hover:opacity-100 transition-opacity">
            <div className="w-8 h-8 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center">
              <Pause size={12} className="text-white" fill="white" />
            </div>
          </div>
        )}
        {playing && (
          <button
            onClick={toggleMute}
            data-testid={`podcast-mute-${idx}`}
            className="absolute bottom-20 right-3 w-9 h-9 rounded-full bg-black/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity z-10"
          >
            {muted ? <VolumeX size={14} className="text-white" /> : <Volume2 size={14} className="text-white" />}
          </button>
        )}
        <div className="absolute bottom-0 left-0 right-0 px-5 pb-5 pointer-events-none">
          <p className="text-white text-base font-semibold tracking-wide">{ep.name}</p>
          <p className="text-[#D4AF37] text-xs tracking-wider uppercase mt-0.5 font-medium">{ep.role}</p>
        </div>
      </div>
    </div>
  );
};

const ExpertConversationsSection = () => (
  <section
    data-testid="expert-conversations-section"
    className="py-20 md:py-28 relative overflow-hidden"
    style={{ background: "linear-gradient(180deg, #F0F5F1 0%, #FEFDFB 50%, #F0F5F1 100%)" }}
  >
    {/* Left side HD food border */}
    <div className="absolute left-0 top-0 bottom-0 w-[280px] hidden xl:block pointer-events-none">
      <img
        src="https://images.unsplash.com/photo-1579150877125-53b49ce88315?auto=format&fit=crop&w=600&h=1200&q=80"
        alt=""
        className="w-full h-full object-cover opacity-[0.12]"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-r from-transparent to-[#F5F9F6]" />
    </div>
    {/* Right side HD food border */}
    <div className="absolute right-0 top-0 bottom-0 w-[280px] hidden xl:block pointer-events-none">
      <img
        src="https://images.unsplash.com/photo-1490645935967-10de6ba17061?auto=format&fit=crop&w=600&h=1200&q=80"
        alt=""
        className="w-full h-full object-cover opacity-[0.12]"
        aria-hidden="true"
      />
      <div className="absolute inset-0 bg-gradient-to-l from-transparent to-[#F5F9F6]" />
    </div>

    <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
      <AnimatedSection>
        <div className="text-center mb-14">
          <span className="inline-flex items-center gap-1.5 text-xs tracking-[0.2em] uppercase font-semibold text-[#D4AF37]">
            <Leaf size={12} />
            Insights From Experts
          </span>
          <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl leading-tight tracking-tight text-[#0D1F17] mt-3">
            Expert <span className="italic font-light">Conversations</span>
          </h2>
          <p className="text-[#5A6B5E] mt-4 max-w-xl mx-auto text-base">
            In-depth conversations with leading doctors on fertility, hormonal health and wellness.
          </p>
        </div>
      </AnimatedSection>

      {/* Mobile: horizontal scroll */}
      <div className="flex gap-5 overflow-x-auto snap-x snap-mandatory scrollbar-hide pb-4 sm:hidden" data-testid="podcast-mobile">
        {episodes.map((ep, idx) => (
          <EpisodeCard key={ep.src} ep={ep} idx={idx} />
        ))}
      </div>

      {/* Desktop: centered grid */}
      <div className="hidden sm:grid grid-cols-2 lg:grid-cols-4 gap-6 max-w-5xl mx-auto" data-testid="podcast-desktop">
        {episodes.map((ep, idx) => (
          <AnimatedSection key={ep.src} delay={idx * 0.1}>
            <EpisodeCard ep={ep} idx={idx} />
          </AnimatedSection>
        ))}
      </div>
    </div>
  </section>
);

export default ExpertConversationsSection;
